import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { type SchemaSubDelay, type SubscribeContactsApiArg } from '@/__generated/es-api'
import {
  ERROR_SUBSCRIBE_GROUP_TO_CAMPAIGN_SERVER,
  ERROR_SUBSCRIBE_SEGMENT_TO_CAMPAIGN_SERVER,
} from '@/app/module/campaigns/definitions'
import { createNotification } from '@/app/module/notifications/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi, getErrorMessage } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'

import { getGroupSegmentLabel } from './group-segment-label'

export type SubscribeGroupsPayload = {
  allowResubscribe?: boolean
  delay?: SchemaSubDelay
  groupIds: number[]
  isSegment: boolean
  startAt?: string
  t0?: string
  t0timezone?: string
}

export const useSubscribeGroups = () => {
  const dispatch = useAppDispatch()
  const orgId = useSelector(selectOrgId)
  const [subscribeContacts, res] = esApi.endpoints.subscribeContacts.useMutation()

  const subscribeGroupsToCampaign = useCallback(
    async (campaignId: number, payload: SubscribeGroupsPayload) => {
      if (payload.groupIds.length === 0) {
        return undefined
      }

      const body: SubscribeContactsApiArg['body'] = {
        allowResubscribe: payload.allowResubscribe,
        args: {
          startAt: payload.startAt,
          t0: payload.t0,
          t0timezone: payload.t0timezone,
        },
        contacts: {
          includedGroupIds: payload.groupIds,
        },
        delay: payload.delay,
      }

      try {
        const result = await subscribeContacts({
          orgId: orgId.toString(),
          campaignId: campaignId.toString(),
          body,
        }).unwrap()

        dispatch(
          createNotification({
            'campaign-subscribe-groups': {
              type: 'success',
              message: `${getGroupSegmentLabel(payload.isSegment, payload.groupIds.length)} successfully subscribed to campaign.`,
            },
          }),
        )
        return result
      } catch (error) {
        dispatch(
          createNotification({
            'campaign-subscribe-groups-failure': {
              type: 'error',
              message: getErrorMessage(
                error,
                payload.isSegment
                  ? ERROR_SUBSCRIBE_SEGMENT_TO_CAMPAIGN_SERVER
                  : ERROR_SUBSCRIBE_GROUP_TO_CAMPAIGN_SERVER,
              ),
            },
          }),
        )
        return undefined
      }
    },
    [dispatch, orgId, subscribeContacts],
  )

  return [subscribeGroupsToCampaign, res] as const
}
